import { guardDialog } from '@adornis/dialog/dialog-functions.js';
import { FileInput } from '@adornis/file-utils/client/file-input.js';
import { CheckboxInput } from '@adornis/formfield/fields/checkbox-input.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { useForm } from '@adornis/forms/use-form.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { html } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { deleteCoursePage, getCoursePages, reorderCoursePages } from '../api/lms-queries.js';
import { LMSAnswerOption, LMSCoursePage, LMSCoursePageType } from '../entities/LMSCoursePage.js';
import '@adornis/dragdrop/x-drag-drop.js';

const PAGE_TYPE_OPTIONS = [
  { value: LMSCoursePageType.VIDEO, label: 'Video' },
  { value: LMSCoursePageType.QUESTION, label: 'Multiple Choice Frage' },
];

const AnswerOptionEditor = stateful(
  ({
    options,
    field,
    onAdd,
    onRemove,
  }: {
    options: LMSAnswerOption[];
    field: (...path: any[]) => any;
    onAdd: () => void;
    onRemove: (index: number) => void;
  }) => html`
    <div class="flex flex-col gap-2">
      <label class="label"><span class="label-text font-medium">Options</span></label>
      ${options.map(
        (opt, i) => html`
          <div class="flex items-center gap-2 p-2 rounded-lg border border-base-300 bg-base-100">
            ${CheckboxInput({
              ...field('answerOptions', i, 'isCorrect'),
              label: '',
            })}
            ${TextInput({
              ...field('answerOptions', i, 'text'),
              placeholder: `Option ${i + 1}`,
            })}
            <button class="btn btn-ghost btn-xs btn-square" @click=${() => onRemove(i)}>
              <span class="icon-[mdi--close] text-base"></span>
            </button>
          </div>
        `,
      )}
      <button class="btn btn-outline btn-sm gap-1 self-start" @click=${onAdd}>
        <span class="icon-[mdi--plus] text-base"></span>
        Add options
      </button>
      <p class="text-xs text-base-content/50">Active options = correct answers</p>
    </div>
  `,
);

const PageEditor = stateful(
  ({
    page,
    onSave,
    onCancel,
  }: {
    page: LMSCoursePage;
    onSave: (page: LMSCoursePage) => Promise<void>;
    onCancel: () => void;
  }) => {
    const [saving, setSaving] = useState(false);
    const [update, setUpdate] = useState(0);
    const [initialPage] = useState(page);

    const { document, field, fileRegistry, uploadAllPendingFiles } = useForm<LMSCoursePage>(initialPage);

    const isNew = !page._id;
    const isQuestion = document.type === LMSCoursePageType.QUESTION;

    const handleSave = async () => {
      setSaving(true);
      try {
        await uploadAllPendingFiles();
        await onSave(document);
      } finally {
        setSaving(false);
      }
    };

    return html`
      <div class="card bg-base-100 border border-primary/30 shadow-sm w-full">
        <div class="card-body p-4 gap-3">
          <div class="flex items-center justify-between">
            <h4 class="font-semibold text-sm">${isNew ? 'New Page' : 'Edit Page'}</h4>
            <div class="flex gap-2">
              <button class="btn btn-ghost btn-sm" @click=${onCancel}>Cancel</button>
              <button class="btn btn-primary btn-sm" ?disabled=${saving} @click=${handleSave}>
                ${saving ? html`<span class="loading loading-spinner loading-xs"></span>` : isNew ? 'Create' : 'Save'}
              </button>
            </div>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div class="form-control w-full">
              <label class="label"><span class="label-text">Type</span></label>
              ${DropdownSelectionInput<(typeof PAGE_TYPE_OPTIONS)[number]>({
                multi: false,
                placeholder: 'Select type…',
                selectables: PAGE_TYPE_OPTIONS,
                value: PAGE_TYPE_OPTIONS.find(o => o.value === document.type),
                renderString: o => o.label,
                resolveID: o => o.value,
                onPicked: value => {
                  document.type = value?.value;
                  setUpdate(update + 1);
                },
              })}
            </div>

            <div class="form-control w-full">
              <label class="label"><span class="label-text">Title</span></label>
              ${TextInput({
                ...field('title'),
                placeholder: isQuestion ? 'e.g. Safety Question 1' : 'e.g. Introduction Video',
              })}
            </div>
          </div>

          ${isQuestion
            ? html`
                <div class="form-control w-full">
                  <label class="label"><span class="label-text">Question Text</span></label>
                  ${TextInput({
                    ...field('questionText'),
                    placeholder: 'e.g. What is the correct answer?',
                  })}
                </div>
                ${AnswerOptionEditor({
                  options: document.answerOptions ?? [],
                  field,
                  onAdd: () => {
                    document.answerOptions ??= [];
                    document.answerOptions.push(new LMSAnswerOption({ text: '', isCorrect: false }));
                    setUpdate(update + 1);
                  },
                  onRemove: (index: number) => {
                    document.answerOptions = (document.answerOptions ?? []).filter((_, i) => i !== index);
                    setUpdate(update + 1);
                  },
                })}
              `
            : html`
                <div class="form-control w-full">
                  <label class="label"><span class="label-text">Video</span></label>
                  ${FileInput({
                    ...field('videoUrl'),
                    fileRegistry,
                    placeholder: 'Select video…',
                    accept: 'video/*',
                    clearable: true,
                  })}
                </div>
              `}
        </div>
      </div>
    `;
  },
);

export const CoursePageEditorSection = stateful(({ courseId }: { courseId: string }) => {
  const [refreshKey, setRefreshKey] = useState(0);
  const [editingPageId, setEditingPageId] = useState<string | null>(null);
  const [creatingNew, setCreatingNew] = useState(false);

  const pagesQuery = useQuery(
    () =>
      getCoursePages(courseId)({
        _id: 1,
        courseId: 1,
        order: 1,
        type: 1,
        title: 1,
        videoUrl: 1,
        questionText: 1,
        answerOptions: { text: 1, isCorrect: 1 },
      }),
    [courseId, refreshKey],
  );

  if (pagesQuery.error) {
    return html`<p class="text-sm text-base-content/50">${pagesQuery.error.message}</p>`;
  }

  const pages = pagesQuery.value ?? [];

  const handleSavePage = async (page: LMSCoursePage) => {
    await guardDialog(
      async () => {
        if (page._id) {
          await page.save();
        } else {
          page.courseId = courseId;
          page.order = pages.length + 1;
          await page.create();
        }
        setEditingPageId(null);
        setCreatingNew(false);
        setRefreshKey(refreshKey + 1);
      },
      {
        loadingText: 'Saving page…',
        successText: 'Page saved.',
        errorMessagePrefix: 'Page could not be saved',
      },
    );
  };

  const handleDeletePage = async (page: LMSCoursePage) => {
    await guardDialog(
      async () => {
        await deleteCoursePage(page._id!)();
        setRefreshKey(refreshKey + 1);
      },
      {
        loadingText: 'Deleting page…',
        successText: 'Page deleted.',
        errorMessagePrefix: 'Page could not be deleted',
      },
    );
  };

  const handleResort = async (e: CustomEvent) => {
    const newOrder: string[] = e.detail.order;
    await guardDialog(
      async () => {
        await reorderCoursePages(courseId, newOrder)();
        setRefreshKey(refreshKey + 1);
      },
      {
        loadingText: 'Reordering pages…',
        successText: 'Pages reordered.',
        errorMessagePrefix: 'Pages could not be reordered',
      },
    );
  };

  if (pagesQuery.loading && pages.length === 0) {
    return html`<span class="loading loading-spinner loading-sm"></span>`;
  }

  return html`
    <div class="flex flex-col gap-3 w-full">
      ${pages.length === 0 ? html`<p class="text-sm text-base-content/50">No pages created yet.</p>` : ''}
      <x-drag-drop
        dropzone
        allow-from="[drag-handle]"
        child-selector="[movable]"
        key-attribute="key"
        drag-mode="column"
        @resorted=${handleResort}
      >
        <div class="flex w-full flex-col gap-3">
          ${repeat(
            pages,
            page => page._id,
            (page, i) =>
              editingPageId === page._id
                ? PageEditor({
                    page,
                    onSave: handleSavePage,
                    onCancel: () => setEditingPageId(null),
                  })
                : html`
                    <div
                      movable
                      key=${page._id!}
                      class="flex items-center gap-3 p-3 rounded-lg border border-base-300 bg-base-100"
                    >
                      <span
                        drag-handle
                        class="icon-[mdi--drag] text-lg text-base-content/40 cursor-grab active:cursor-grabbing"
                      ></span>
                      <span class="badge badge-sm badge-neutral">${i + 1}</span>
                      <span
                        class="${page.type === LMSCoursePageType.VIDEO
                          ? 'icon-[mdi--play-circle-outline]'
                          : 'icon-[mdi--help-circle-outline]'} text-lg text-base-content/60"
                      ></span>
                      <div class="flex-1 min-w-0">
                        <p class="font-medium text-sm truncate">
                          ${page.title ?? (page.type === LMSCoursePageType.VIDEO ? 'Video' : 'Question')}
                        </p>
                        <p class="text-xs text-base-content/50">
                          ${page.type === LMSCoursePageType.VIDEO
                            ? 'Video'
                            : `MC Question · ${page.answerOptions?.length ?? 0} Options`}
                        </p>
                      </div>
                      <button class="btn btn-ghost btn-xs btn-square" @click=${() => setEditingPageId(page._id!)}>
                        <span class="icon-[mdi--pencil] text-base"></span>
                      </button>
                      <button class="btn btn-ghost btn-xs btn-square text-error" @click=${() => handleDeletePage(page)}>
                        <span class="icon-[mdi--delete-outline] text-base"></span>
                      </button>
                    </div>
                  `,
          )}
        </div>
      </x-drag-drop>
      ${creatingNew
        ? PageEditor({
            page: new LMSCoursePage({
              courseId,
              type: LMSCoursePageType.VIDEO,
              order: pages.length + 1,
            }),
            onSave: handleSavePage,
            onCancel: () => setCreatingNew(false),
          })
        : html`
            <button class="btn btn-outline btn-sm gap-1 self-start" @click=${() => setCreatingNew(true)}>
              <span class="icon-[mdi--plus] text-base"></span>
              Add page
            </button>
          `}
    </div>
  `;
});
